/**
 * PaperStatsPanel — 시험지 왼쪽 열. **이 시험지가 무엇으로 이뤄졌는지**를 숫자로 말한다.
 *
 * 오른쪽 열이 문항을 하나씩 보여 주는 자리라면 여기는 통째로 보는 자리다. 200문항을 스크롤해
 * 훑어야 알 수 있던 것 — 난이도가 한쪽에 몰렸는지, 어느 영역이 비었는지, 어떤 개념이 반복되는지 —
 * 을 열 하나에 세운다.
 *
 * ── 얼개 (오너 지시 2026-08-28) ───────────────────────────────────────────
 *   머리 : 전체 문항 수 · 폴더 · 판
 *   위   : 난이도 · 영역 · 개념 유형을 **3열로** 나란히. 셋 다 줄 수가 정해져 있어 자기 높이만 갖는다
 *   아래 : **모든 개념**을 등장 건수 내림차순으로. 남는 높이를 전부 갖고, **넘치면 여기만 스크롤**
 *
 * 3열로 눕힌 것이 요점이다. 세로로 쌓으면 세 분포가 열세 줄을 먹어 개념 목록이 설 자리가 없었다.
 * 나란히 세우면 다섯 줄로 줄고, 아낀 그만큼이 그대로 개념 목록의 높이가 된다.
 * 좌측 칸이 좁으면 다시 세로로 쌓는다 — 그 폭에서 나란히 세우면 막대가 라벨과 수 사이에서
 * 사라져 세 칸이 그냥 숫자 목록이 된다.
 *
 * ── 넓을 때 — 두 칸으로 나눈다 (오너 결정 2026-09-15 · split) ──────────────
 *   왼쪽  : 머리 → 분포 세 구획을 **세로로**, 칸 높이에 맞춰 벌려 세운다(D2 B) → 미비 줄
 *   오른쪽: 개념 목록이 칸 높이 전부를 쓰고, 넘치면 여기만 스크롤
 * 개념 목록이 옆 칸으로 빠지면 세로 예산이 칸 전체가 되므로, 위 「3열로 눕힌 이유」가 사라진다.
 * 나눌지는 이 부품이 재지 않는다 — 편집기가 paperItemsLayout.js 로 정해 넘긴다(카드 2열이 먼저 선 뒤에만).
 * 안 나눌 때의 마크업은 종전 그대로다.
 *
 * ── 이 부품이 지는 책임 ───────────────────────────────────────────────────
 * 집계는 하지 않는다(paperStats.js 가 한다). 여기가 지는 것은 **모르는 것을 모른다고 말하는 일**이다.
 *  · 서버 문항을 못 읽었으면(itemsUnknown) 숫자를 **아예 그리지 않는다.** 0 을 그리면 바로 위
 *    경고 띠의 「문항이 남아 있습니다」와 화면이 서로 다른 말을 한다.
 *  · 개념 사전을 못 받았으면 영역·유형 칸을 **통째로 감춘다.** 0 으로 그리면 「기하 0개」라는
 *    거짓말이 된다 — 없는 것이 아니라 못 읽은 것이다.
 *  · 메타를 못 받은 문항이 있으면 **그 수를 적는다.** 200 중 40 이 빠진 채 「대수 12」라고만
 *    쓰면 화면이 조용히 거짓말을 한다.
 *
 * ── 값 ────────────────────────────────────────────────────────────────────
 * 글자 크기를 rem 클래스(text-xs·text-sm)가 아니라 **px 임의값**으로 박는다. 이 저장소의
 * index.css 가 768px 이하에서 `.text-xs { font-size: .65rem !important }` 를 걸고 html 을 90% 로
 * 줄여, 그 조합이 **9.36px** 이 된다(한글 하한 미달). px 로 박으면 그 규칙이 닿지 않는다.
 * 척도는 CLAUDE.md 6단 안에서 고른다 — 34(KPI 숫자) · 15(라벨) · 12(캡션).
 * 색은 먹색 3단의 이 앱 대응값 — gray-800(본문·값) · gray-600(보조) · gray-400(선·아이콘 전용).
 */
import { useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import { ConceptBadge, CONCEPT_TYPE_COLOR } from './conceptBadge';
import { MATH_TYPE_COLOR } from '../common/report/reportColors';
// 난이도 색은 [전체 보기] 모달·문제 카드가 쓰는 **바로 그 값**이다. 같은 난이도가 좌측 막대와
// 미리보기에서 다른 색이면 그때부터 색이 정보가 아니라 장식이 된다.
import { DIFFICULTY_COLORS } from '../../pages/LearningStatus/utils/constants';

const difficultyColor = (d) => DIFFICULTY_COLORS[d - 1] || null;

/** 한 칸으로 쌓는 몸체 — 머리·분포·미비 줄은 제 높이, 개념 목록이 남는 높이를 전부 갖는다 */
const STACK_ROOT = 'flex h-full flex-col gap-2 overflow-hidden rounded-2xl border border-gray-200 bg-white p-4';
/**
 * 두 칸으로 나눈 몸체 — 행 하나를 몸체 높이만큼 늘려 두 칸이 같은 높이로 선다.
 * 여백 p-4(32)·테두리(2)·칸 사이 gap-x-4(16)는 paperItemsLayout 의 나눔 문턱 셈에 들어 있다 — 바꾸면 거기도 바꾼다.
 */
const SPLIT_ROOT = 'grid h-full grid-cols-2 grid-rows-[minmax(0,1fr)] gap-x-4 overflow-hidden rounded-2xl border border-gray-200 bg-white p-4';

/**
 * 나눴을 때의 왼쪽 칸 — 머리·분포·미비 줄을 한 칸에 세로로 담는다. 안 나누면 **아무것도 두르지 않는다**
 * (자식을 그대로 흘려 한 칸 몸체의 직계 자식이 된다 — 종전 마크업 그대로다. ProblemBodyPreview 의 Wrap 과 같은 모양).
 * 창이 낮아 세 구획이 칸을 넘치면 **이 칸 안에서만** 스크롤한다
 * (2026-09-15 실측: 머리 + 세 구획 + 미비 줄 ≈ 534px — 창 높이 1080 이면 들어가고 950 이면 넘친다).
 */
function LeftColumn({ split, children }) {
  if (!split) return children;
  return <div className="flex min-h-0 min-w-0 flex-col gap-2 overflow-y-auto">{children}</div>;
}

/** 분포 한 칸 — 3열의 한 자리. 줄 수가 정해져 있어 자기 높이만 갖는다 */
function StatBlock({ title, caption = null, className = '', children }) {
  return (
    <section className={className ? `min-w-0 ${className}` : 'min-w-0'}>
      <h4 className="truncate text-[12px] font-bold text-gray-600">
        {title}
        {caption && <span className="ml-1 font-normal text-gray-400">{caption}</span>}
      </h4>
      <ul className="mt-1 flex flex-col gap-1">{children}</ul>
    </section>
  );
}

/**
 * 가로 막대 한 줄 — [라벨][막대][수].
 *
 * 막대 폭은 **그 칸 안에서 가장 큰 값**을 100% 로 잡는다. 전체 합을 기준으로 하면 값이 고르게
 * 퍼진 시험지에서 막대가 전부 짧아져 차이가 안 보인다. 수는 막대 옆에 먹색으로 적는다 —
 * 색 면 위에 흰 글자를 얹으면 대비가 2.4:1 안팎으로 내려간다.
 *
 * 0 인 줄도 그린다(그 영역이 비었다는 것이 정보다). 그때는 막대 자리에 트랙만 남는다.
 */
function BarRow({ label, count, max, color, badge = null }) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <li className="flex items-center gap-1.5">
      {/* 라벨은 **줄어든다.** 고정폭(w-14)이면 칸이 좁아질 때 막대가 먼저 0 이 되어
          세 칸이 그냥 숫자 목록이 된다. 최대 56 까지만 쓰고 그 아래로는 스스로 줄어든 뒤
          글자를 자른다 — 잘려도 배지 색과 자리로 어느 줄인지 알아본다. */}
      <span className="flex min-w-0 max-w-[56px] shrink items-center gap-1 text-[12px] text-gray-600" title={label}>
        {badge}
        <span className="truncate">{label}</span>
      </span>
      {/* 막대는 최소 16px 를 지킨다 — 그 아래로 줄면 색 점이 되어 길이를 못 읽는다 */}
      <span className="h-2 min-w-[16px] flex-1 overflow-hidden rounded-full bg-gray-100">
        {/* 0 이면 아무것도 그리지 않는다 — 1px 짜리 막대가 남으면 '조금 있다'로 읽힌다 */}
        {count > 0 && (
          <span
            className="block h-full rounded-full"
            style={{ width: `${Math.max(pct, 4)}%`, backgroundColor: color }}
          />
        )}
      </span>
      <span className={`w-5 shrink-0 text-right text-[12px] tabular-nums ${count > 0 ? 'font-bold text-gray-800' : 'text-gray-400'}`}>
        {count}
      </span>
    </li>
  );
}

function PaperStatsPanel({
  stats,
  itemsUnknown = false,
  onRetryMeta = null,
  onConceptClick = null,
  split = false,
}) {
  const maxDifficulty = useMemo(
    () => Math.max(0, ...Object.values(stats?.difficulty || {})),
    [stats],
  );
  const maxMath = useMemo(
    () => Math.max(0, ...(stats?.mathTypes || []).map((m) => m.count)),
    [stats],
  );
  const maxType = useMemo(
    () => Math.max(0, ...(stats?.conceptTypes || []).map((t) => t.count)),
    [stats],
  );

  // 서버 문항을 못 읽은 상태 — 숫자를 그리면 「0개」와 「문항이 남아 있다」가 한 화면에서
  // 서로 다른 말을 한다. 그래서 통계 자리를 통째로 비우고 그 사실만 적는다.
  if (itemsUnknown) {
    return (
      <div className="h-full rounded-2xl border border-amber-200 bg-amber-50 p-4">
        <p className="text-[12px] leading-relaxed text-amber-900">
          서버가 준 문항 목록을 읽지 못해 <b>집계할 수 없습니다.</b>
          <br />
          시험지에는 문항이 남아 있습니다 — 화면이 비어 보이는 것과 다릅니다.
        </p>
      </div>
    );
  }

  const {
    total, deleted, missing, pending,
    difficulty, difficultyNone,
    need, mathTypes, conceptTypes, concepts,
    conceptTagTotal, uniqueConcepts, dictReady,
  } = stats;

  const needLines = [
    need.concept > 0 ? `개념 ${need.concept}` : null,
    need.answer > 0 ? `정답 ${need.answer}` : null,
    need.solution > 0 ? `풀이 ${need.solution}` : null,
  ].filter(Boolean);

  // 개념이 하나도 없으면 나누지 않는다 — 오른쪽 칸이 통째로 비고, 분포만 반쪽 폭에 갇힌다
  const splitOn = split && concepts.length > 0;
  // 나눈 뒤 영역·개념 유형 구획 머리에 긋는 선 — 미비 줄·개념 목록 머리의 구획선과 같은 선이다
  const blockDivider = splitOn ? 'border-t border-gray-100 pt-1.5' : '';

  return (
    /* h-full — 담는 쪽이 준 높이를 그대로 쓴다. 머리와 분포 3열은 자기 높이만 갖고,
       남는 높이는 개념 목록이 전부 가져가 그 안에서만 스크롤한다.
       나눴을 때(splitOn)는 두 칸이 같은 높이로 서고, 개념 목록은 오른쪽 칸 높이 전부를 쓴다. */
    <div className={splitOn ? SPLIT_ROOT : STACK_ROOT}>
      <LeftColumn split={splitOn}>
        {/* ── 머리 — 문항 수 하나만 크게 ───────────────────────────────────────
               폴더와 판(rev)은 **여기서 뺐다**(오너 지시 2026-08-28 「이 두 요소는 중복이므로 제거」).
               상세 패널의 머리줄이 이미 그 둘을 말한다(ExamPapers 의 detailTitle · paperHeadMeta) —
               한 화면에서 같은 값을 두 번 적으면 둘이 갈렸을 때 어느 쪽이 참인지 알 수 없다. */}
        <div className="shrink-0">
          <div className="flex items-baseline gap-2">
            <h4 className="shrink-0 text-[12px] font-bold text-gray-600">전체 문항</h4>
            <span className="text-[34px] font-extrabold tabular-nums leading-none text-gray-800">{total}</span>
            <span className="shrink-0 text-[15px] text-gray-600">개</span>
          </div>
          {/* 못 받은 문항 — 분포의 모수를 밝히는 줄이다. 머리에 두는 것은 아래 세 칸에 함께
              걸리는 사실이기 때문이다(칸마다 되풀이하면 같은 말을 세 번 한다). */}
          {(missing > 0 || pending > 0) && (
            <p className="mt-1 text-[12px] leading-snug text-gray-600">
              {pending > 0 && `${pending}개는 읽는 중. `}
              {missing > 0 && (
                <>
                  {`${missing}개는 못 받아 집계에서 빠졌습니다. `}
                  {onRetryMeta && (
                    <button type="button" onClick={onRetryMeta} className="text-primary underline">다시 시도</button>
                  )}
                </>
              )}
            </p>
          )}
        </div>

        {/* ── 분포 3열 — 난이도 · 영역 · 개념 유형 ─────────────────────────────
               셋 다 줄 수가 정해져 있어(5 · 4~5 · 최대 8) 자기 높이만 갖는다. 개념 유형은
               여기서 자르지 않는다 — 나란히 세우면 세로 예산이 세 배라 여덟 줄도 들어간다.

               **폭을 재서 접지 않는다.** 처음에는 좁으면 세로로 쌓게 했는데, 실제 화면에서 좌측이
               예상보다 좁아 그대로 1열이 됐다(오너 지적 2026-08-28 「이 요소 세개를 3열로
               배치해달라는건데 뭘 한거지?」). 3열은 요구이지 폭에 따른 선택이 아니다 —
               대신 좁아질 때 무너지지 않도록 라벨을 줄어들게 만들었다(BarRow).

               **나눴을 때(넓을 때)의 세로는 이것과 다른 일이다**(오너 결정 2026-09-15). 좁아서 접는
               것이 아니라, 개념 목록이 옆 칸으로 빠져 세로 예산이 칸 전체가 되었기 때문에 세운다.
               세 구획은 칸 높이에 맞춰 벌려 선다(justify-between · 참고 이미지와 같은 모양, D2 B).
               min-h-0 을 두지 않는 것이 요점이다 — 두면 낮은 창에서 구획이 칸보다 작게 눌려 미비 줄
               위로 넘친다. 기본 min-height:auto 가 내용 높이 아래로 줄지 않게 막고, 넘침은 왼쪽 칸
               스크롤(LeftColumn)이 받는다. */}
        <div className={splitOn ? 'flex flex-1 flex-col justify-between gap-2' : 'grid shrink-0 grid-cols-3 gap-3'}>
          <StatBlock title="난이도">
            {[1, 2, 3, 4, 5].map((lv) => (
              <BarRow
                key={lv}
                label={`Lv.${lv}`}
                count={difficulty[lv]}
                max={maxDifficulty}
                color={difficultyColor(lv)}
              />
            ))}
          </StatBlock>

          {/* 사전이 없으면 두 칸을 통째로 감춘다 — 0 으로 그리면 「기하 0개」라는 거짓말이 된다 */}
          {dictReady && conceptTagTotal > 0 && (
            <StatBlock title="영역" caption={`태그 ${conceptTagTotal}`} className={blockDivider}>
              {mathTypes.map((m) => (
                <BarRow
                  key={m.code}
                  label={m.label}
                  count={m.count}
                  max={maxMath}
                  color={MATH_TYPE_COLOR[m.code] || MATH_TYPE_COLOR._}
                />
              ))}
            </StatBlock>
          )}

          {dictReady && conceptTypes.length > 0 && (
            <StatBlock title="개념 유형" className={blockDivider}>
              {conceptTypes.map((t) => (
                <BarRow
                  key={t.code}
                  label={t.label}
                  count={t.count}
                  max={maxType}
                  color={CONCEPT_TYPE_COLOR[t.code]}
                  badge={
                    <span
                      className="inline-flex items-center justify-center rounded-md text-[7px] font-semibold text-gray-800"
                      style={{ width: 20, height: 14, minWidth: 20, backgroundColor: CONCEPT_TYPE_COLOR[t.code] }}
                    >
                      {t.code}
                    </span>
                  }
                />
              ))}
            </StatBlock>
          )}
        </div>

        {/* ── 경고·미비 — 셋 다 「이대로 두면 무슨 일이 나는가」를 말하는 줄이라 한자리에 모은다 ── */}
        {(difficultyNone > 0 || deleted > 0 || needLines.length > 0) && (
          <div className="shrink-0 space-y-0.5 border-t border-gray-100 pt-1.5">
            {deleted > 0 && (
              <p className="flex items-start gap-1 text-[12px] leading-snug text-amber-800">
                <AlertTriangle aria-hidden="true" className="mt-0.5 size-3 shrink-0" />
                <span>원본이 삭제된 문항 <b>{deleted}개</b> — 빼야 배포됩니다.</span>
              </p>
            )}
            {difficultyNone > 0 && (
              /* 계약 §1-3 — 난이도(분석)가 없는 문항이 있으면 배포가 409 missing_difficulty 로
                 막힌다. 눌러 보고 막히는 것보다 여기서 먼저 말한다. */
              <p className="flex items-start gap-1 text-[12px] leading-snug text-amber-800">
                <AlertTriangle aria-hidden="true" className="mt-0.5 size-3 shrink-0" />
                <span>난이도 없는 문항 <b>{difficultyNone}개</b> — 이대로면 <b>배포가 막힙니다.</b></span>
              </p>
            )}
            {/* need_* 는 검색 응답이 실어 주는 「분석이 덜 됐다」 표시다(계약 §1-5) */}
            {needLines.length > 0 && (
              <p className="text-[12px] leading-snug text-gray-600">
                아직 안 채워진 것 — <span className="text-gray-800">{needLines.join(' · ')}</span>
              </p>
            )}
          </div>
        )}
      </LeftColumn>

      {/* ── 개념 — **전부**, 많이 나온 순. 남는 높이를 전부 갖고 넘치면 여기만 스크롤한다
             (오너 지시 2026-08-28 「모든 매스컨셉별 등장 건수를 내림차순으로, 넘치면 내부 스크롤」).
             배지는 목록 행과 같은 부품이라 같은 개념이 자리에 따라 다른 얼굴이 되지 않는다.
             나눴을 때는 오른쪽 칸이다 — 위 선 대신 왼쪽 세로선을 긋는다. 선 1 + 여백 16 = 17 이
             paperItemsLayout 의 STATS_DIVIDER_PX 다(나눔 문턱 셈에 들어 있다). */}
      {concepts.length > 0 && (
        <section
          className={splitOn
            ? 'flex min-h-0 min-w-0 flex-col border-l border-gray-100 pl-4'
            : 'flex min-h-0 flex-1 flex-col border-t border-gray-100 pt-1.5'}
        >
          <h4 className="shrink-0 text-[12px] font-bold text-gray-600">
            개념
            <span className="ml-1 font-normal text-gray-400">서로 다른 {uniqueConcepts}개</span>
          </h4>
          {/* pr-1 은 스크롤바 몫이다 — 없으면 막대가 배지 오른쪽 끝을 덮는다 */}
          <ul className="mt-1 min-h-0 flex-1 space-y-1 overflow-y-auto pr-1">
            {concepts.map((c) => (
              <li key={c.id ?? c.name} className="flex items-center gap-1.5">
                <span className="min-w-0 flex-1 overflow-hidden">
                  <ConceptBadge
                    name={c.name}
                    order={c.order}
                    type={c.type}
                    compact
                    onClick={c.id != null && onConceptClick ? () => onConceptClick(c.id) : null}
                  />
                </span>
                <span className="shrink-0 text-[12px] font-bold tabular-nums text-gray-800">{c.count}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

export default PaperStatsPanel;
